import React, { useState, useEffect } from "react";
import { Layout, Menu, Badge } from "antd";
import { sidebarItems } from "./SidebarData";
import { Link, useLocation } from "react-router-dom";
import { useRole } from "../../../hooks/useRole";
import {
  useGetSidebarCountQuery,
  useMarkAsViewedMutation,
} from "../../../redux/api/SidebarActivity/SidebarActivityApi";

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = () => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const { user: currentUser } = useRole();
  const { data: notificationCounts, refetch } = useGetSidebarCountQuery(
    { userId: currentUser?.id },
    {
      pollingInterval: 60000,
      skip: !currentUser?.id,
    }
  );

  const [markModuleAsViewed] = useMarkAsViewedMutation();

  // Map of URL paths to module names
  const moduleMapping = {
    "/admin/bookings": "bookings",
    "/admin/enquiry": "enquiry",
    "/admin/prescription": "prescription",
    "/admin/issues": "issues",
    "/admin/testimonial": "testimonial",
    "/admin/career-applications": "careers",
  };

  // Mark module as viewed when user navigates to it
  useEffect(() => {
    const currentModule = moduleMapping[location.pathname];

    if (currentModule && currentUser?.id) {
      // Mark as viewed and refetch counts
      markModuleAsViewed({
        userId: currentUser.id,
        module: currentModule,
      }).then(() => {
        // Refetch notification counts after marking as viewed
        refetch();
      });
    }
  }, [location.pathname, currentUser?.id, markModuleAsViewed, refetch]);

  // Use the counts from API or default to 0
  const notifications = {
    bookings: notificationCounts?.bookings || 0,
    enquiry: notificationCounts?.enquiry || 0,
    prescription: notificationCounts?.prescription || 0,
    "issues&complaints": notificationCounts?.issues || 0,
    testimonial: notificationCounts?.testimonial || 0,
    careers: notificationCounts?.careers || 0,
  };

  // Handle menu selection and hierarchy
  const findSelectedKey = (items, path) => {
    for (const item of items) {
      if (item.url === path) {
        return item.id.toString();
      }
      if (item.children) {
        const childKey = findSelectedKey(item.children, path);
        if (childKey) {
          return childKey;
        }
      }
    }
    return null;
  };

  const selectedKey = findSelectedKey(sidebarItems, location.pathname);

  useEffect(() => {
    const findParentKey = (items, path) => {
      for (const item of items) {
        if (item.url === path) {
          return item.id.toString();
        }
        if (item.children) {
          const childKey = findParentKey(item.children, path);
          if (childKey) {
            return item.id.toString();
          }
        }
      }
      return null;
    };

    const parentKey = findParentKey(sidebarItems, location.pathname);
    if (parentKey) {
      setOpenKeys([parentKey]);
    }
  }, [location.pathname]);

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const filteredSidebarItems = React.useMemo(() => {
    if (!currentUser) {
      return [];
    }
    if (currentUser.role === "superadmin") {
      return sidebarItems;
    }
    return sidebarItems.filter((item) => {
      if (item.module === "adminList" && currentUser.role !== "superadmin") {
        return false;
      }
      if (currentUser.role) {
        return currentUser.permissions.includes(item.module);
      }
      return true;
    });
  }, [currentUser]);

  // Function to render menu item with notification badge
  const renderMenuItemWithBadge = (item, isSelected) => {
    const hasNotification = notifications[item.module] > 0;
    const notificationCount = notifications[item.module];

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Link className="link-unstyled" to={item.url} style={{ flex: 1 }}>
          {item.label}
        </Link>
        {hasNotification && (
          <Badge
            count={notificationCount > 99 ? "99+" : notificationCount}
            size="small"
            style={{
              backgroundColor: "#ff4d4f",
              color: "white",
              fontSize: "10px",
              minWidth: "16px",
              height: "16px",
              lineHeight: "16px",
              borderRadius: "8px",
              marginLeft: "8px",
              boxShadow: "0 0 4px rgba(255, 77, 79, 0.5)",
            }}
          />
        )}
      </div>
    );
  };

  if (!currentUser) {
    return <div>Loading...</div>;
  }

  return (
    <Sider
      collapsible={false}
      trigger={null}
      width={200}
      className="site-layout-background d-none d-lg-block"
      style={{
        borderRight: "2px solid #e9edf4",
        position: "fixed",
        height: "100vh",
        overflowY: "scroll",
        background: "#ffffff",
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        style={{ height: "100%", borderRight: 0 }}
        theme="light"
      >
        {filteredSidebarItems.map((menu) =>
          menu.children && menu.children.length > 0 ? (
            <SubMenu
              key={menu.id}
              icon={menu.icon}
              style={{
                color:
                  selectedKey === menu.id.toString() ||
                  menu.children.some(
                    (child) => selectedKey === child.id.toString()
                  )
                    ? "var(--primary-color)"
                    : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id.toString() ||
                  menu.children.some(
                    (child) => selectedKey === child.id.toString()
                  )
                    ? "#e0dee2"
                    : "#ffffff",
              }}
              title={<span style={{ color: "#6d758a" }}>{menu.label}</span>}
            >
              {menu.children.map((item) => (
                <Menu.Item
                  key={item.id}
                  icon={item.icon}
                  style={{
                    color:
                      selectedKey === item.id.toString()
                        ? "var(--primary-color)"
                        : "#6d758a",
                    backgroundColor:
                      selectedKey === item.id.toString()
                        ? "#e0dee2"
                        : "#ffffff",
                  }}
                >
                  {renderMenuItemWithBadge(
                    item,
                    selectedKey === item.id.toString()
                  )}
                </Menu.Item>
              ))}
            </SubMenu>
          ) : (
            <Menu.Item
              key={menu.id}
              icon={menu.icon}
              style={{
                color:
                  selectedKey === menu.id.toString()
                    ? "var(--primary-color)"
                    : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id.toString() ? "#e0dee2" : "#ffffff",
                fontWeight: selectedKey === menu.id.toString() ? "600" : "",
              }}
            >
              {renderMenuItemWithBadge(
                menu,
                selectedKey === menu.id.toString()
              )}
            </Menu.Item>
          )
        )}
      </Menu>
    </Sider>
  );
};

export default Sidebar;
